import { useMemo, useRef, useState } from 'react';
import { useTranslation } from '../../../i18n/useTranslation';
import { Alert } from '../../atoms/Alert/Alert';
import { Button } from '../../atoms/Button/Button';
import { Inline } from '../../atoms/Inline/Inline';
import { LiveRegion } from '../../atoms/LiveRegion/LiveRegion';
import { Stack } from '../../atoms/Stack/Stack';
import { Text } from '../../atoms/Typography/Text';
import { ConfirmDialog } from '../../ui/ConfirmDialog/ConfirmDialog';
import { Section } from '../../templates/Section/Section';
import {
  createOverviewLivestock,
  deleteOverviewAnalysis,
  updateOverviewCandidate,
  type OverviewAnalysis,
  type OverviewCreateResult,
} from '../../../services/livestockOverviewService';
import { OverviewCandidateCard, type CandidateDraft } from './OverviewCandidateCard';
import { OverviewPhotoMarkers } from './OverviewPhotoMarkers';
import { formatCredits, formatOverviewDate, localIsoDate, overviewFailureMessage } from './overviewText';
import './LivestockOverview.css';

export interface OverviewReviewProps {
  aquariumId: string;
  analysis: OverviewAnalysis;
  /** Called after the selected animals were created. */
  onCreated: (result: OverviewCreateResult) => void;
  /** Called after the analysis was discarded. */
  onDiscarded: () => void;
}

function initialDrafts(analysis: OverviewAnalysis): Record<string, CandidateDraft> {
  return Object.fromEntries(
    analysis.candidates.map((c) => [
      c.id,
      { selected: c.selected, commonName: c.commonName, scientificName: c.scientificName ?? '', category: c.category },
    ]),
  );
}

/**
 * Review of the found animals: each with its cut-out, editable name and category, the
 * confidence as text and a checkbox (pre-selected for confident suggestions). The review
 * state is kept on the server as the user goes, so a refresh does not lose it. The
 * primary action creates exactly the selected animals in one request.
 */
export function OverviewReview({ aquariumId, analysis, onCreated, onDiscarded }: OverviewReviewProps) {
  const { t, i18n } = useTranslation();
  const [drafts, setDrafts] = useState<Record<string, CandidateDraft>>(() => initialDrafts(analysis));
  const [showErrors, setShowErrors] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const [isDiscarding, setIsDiscarding] = useState(false);
  const alertRef = useRef<HTMLDivElement>(null);
  const nameRefs = useRef(new Map<string, HTMLInputElement>());

  const open = analysis.candidates.filter((c) => !c.created);
  const selectedIds = useMemo(
    () => new Set(analysis.candidates.filter((c) => c.created || drafts[c.id]?.selected).map((c) => c.id)),
    [analysis.candidates, drafts],
  );
  const toCreate = open.filter((c) => drafts[c.id]?.selected);
  const missingName = toCreate.filter((c) => !(drafts[c.id]?.commonName ?? '').trim());

  const change = (id: string, patch: Partial<CandidateDraft>): void => {
    setDrafts((current) => ({ ...current, [id]: { ...(current[id] as CandidateDraft), ...patch } }));
  };

  /** Keeps a change on the server too (best effort — the create request carries the final values). */
  const commit = (id: string, patch: Partial<CandidateDraft>): void => {
    change(id, patch);
    const body = {
      ...(patch.selected !== undefined ? { selected: patch.selected } : {}),
      ...(patch.commonName ? { commonName: patch.commonName } : {}),
      ...(patch.scientificName !== undefined ? { scientificName: patch.scientificName || null } : {}),
      ...(patch.category !== undefined ? { category: patch.category } : {}),
    };
    if (Object.keys(body).length === 0) return;
    updateOverviewCandidate(aquariumId, analysis.id, id, body).catch(() => {
      // Not fatal: the values on screen are sent again when the animals are created.
    });
  };

  const setAll = (selected: boolean): void => {
    for (const candidate of open) {
      if (drafts[candidate.id]?.selected !== selected) commit(candidate.id, { selected });
    }
  };

  const focusAlert = (): void => {
    requestAnimationFrame(() => alertRef.current?.focus());
  };

  const handleCreate = (): void => {
    if (toCreate.length === 0 || isCreating) return;
    setActionError(null);
    if (missingName.length > 0) {
      setShowErrors(true);
      const first = missingName[0];
      requestAnimationFrame(() => (first ? nameRefs.current.get(first.id)?.focus() : undefined));
      return;
    }
    setIsCreating(true);
    createOverviewLivestock(
      aquariumId,
      analysis.id,
      toCreate.map((c) => {
        const draft = drafts[c.id] as CandidateDraft;
        return {
          candidateId: c.id,
          commonName: draft.commonName.trim(),
          scientificName: draft.scientificName.trim() || null,
          category: draft.category,
        };
      }),
      localIsoDate(),
    )
      .then((result) => {
        setIsCreating(false);
        onCreated(result);
      })
      .catch(() => {
        setIsCreating(false);
        setActionError(t('livestockOverview.review.createError'));
        focusAlert();
      });
  };

  const handleDiscard = (): void => {
    setIsDiscarding(true);
    deleteOverviewAnalysis(aquariumId, analysis.id)
      .then(() => {
        setIsDiscarding(false);
        setConfirmDiscard(false);
        onDiscarded();
      })
      .catch(() => {
        setIsDiscarding(false);
        setConfirmDiscard(false);
        setActionError(t('livestockOverview.review.discardError'));
        focusAlert();
      });
  };

  const total = analysis.candidates.length;

  return (
    <Stack gap="8">
      <Stack gap="4">
        {analysis.errorCode && (
          <Alert tone="warning" live="polite">
            {analysis.errorCode === 'AI_BUDGET_EXCEEDED' || analysis.errorCode === 'AI_TEMPORARILY_UNAVAILABLE'
              ? `${t('livestockOverview.review.incomplete')} ${overviewFailureMessage(t, analysis.errorCode)}`
              : t('livestockOverview.review.incomplete')}
          </Alert>
        )}
        <Alert tone="info" live="off">
          {t('livestockOverview.review.limits')}
        </Alert>
        <Text color="muted">
          {t('livestockOverview.review.credits', { credits: formatCredits(analysis.creditsUsed, i18n.language) })}{' '}
          {t('livestockOverview.review.expires', { date: formatOverviewDate(analysis.expiresAt, i18n.language) })}
        </Text>
      </Stack>

      {analysis.imageUrl && (
        <Section title={t('livestockOverview.review.photoTitle')}>
          <OverviewPhotoMarkers imageUrl={analysis.imageUrl} candidates={analysis.candidates} selectedIds={selectedIds} />
        </Section>
      )}

      <Section
        title={t('livestockOverview.review.sectionTitle', { count: total })}
        description={t('livestockOverview.review.instructions')}
      >
        <Stack gap="4">
          {open.length > 0 && (
            <Inline gap="2" align="center" wrap>
              <Button variant="secondary" size="sm" onClick={() => setAll(true)} disabled={isCreating}>
                {t('livestockOverview.review.selectAll')}
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setAll(false)} disabled={isCreating}>
                {t('livestockOverview.review.selectNone')}
              </Button>
              <LiveRegion as="span">
                <Text as="span" color="muted">
                  {t('livestockOverview.review.selectedCount', { selected: toCreate.length, total: open.length })}
                </Text>
              </LiveRegion>
            </Inline>
          )}

          <ul className="livestock-overview__list" aria-label={t('livestockOverview.review.listLabel')}>
            {analysis.candidates.map((candidate) => {
              const draft = drafts[candidate.id] as CandidateDraft;
              const nameMissing = showErrors && draft.selected && !candidate.created && !draft.commonName.trim();
              return (
                <li key={candidate.id}>
                  <OverviewCandidateCard
                    ref={(el) => {
                      if (el) nameRefs.current.set(candidate.id, el);
                      else nameRefs.current.delete(candidate.id);
                    }}
                    candidate={candidate}
                    draft={draft}
                    nameError={nameMissing ? t('livestockOverview.review.nameRequired') : undefined}
                    onChange={(patch) => change(candidate.id, patch)}
                    onCommit={(patch) => commit(candidate.id, patch)}
                    disabled={isCreating}
                  />
                </li>
              );
            })}
          </ul>

          {showErrors && missingName.length > 0 && (
            <Alert tone="warning" compact>
              {t('livestockOverview.review.invalid')}
            </Alert>
          )}
          {actionError && (
            <Alert ref={alertRef} tabIndex={-1}>
              {actionError}
            </Alert>
          )}
          {isCreating && (
            <LiveRegion busy>
              <Text>{t('livestockOverview.review.creating')}</Text>
            </LiveRegion>
          )}

          <div className="livestock-overview__actions">
            <Button variant="ghost" onClick={() => setConfirmDiscard(true)} disabled={isCreating}>
              {t('livestockOverview.review.discard')}
            </Button>
            <Button
              variant="primary"
              onClick={handleCreate}
              disabled={toCreate.length === 0 || isCreating}
              isLoading={isCreating}
              data-testid="overview-create"
            >
              {toCreate.length === 0
                ? t('livestockOverview.review.createNone')
                : t('livestockOverview.review.create', { count: toCreate.length })}
            </Button>
          </div>
        </Stack>
      </Section>

      <ConfirmDialog
        isOpen={confirmDiscard}
        title={t('livestockOverview.review.discardTitle')}
        message={t('livestockOverview.review.discardMessage')}
        confirmLabel={t('livestockOverview.review.discardConfirm')}
        cancelLabel={t('livestockOverview.review.discardCancel')}
        onConfirm={handleDiscard}
        onCancel={() => setConfirmDiscard(false)}
        isConfirming={isDiscarding}
      />
    </Stack>
  );
}

export default OverviewReview;
